/**
 * VueX store for room members.
 *
 * Loads users in batches (groups). Short lists of 50 or fewer users are loaded in one go to not do a lot of requests
 * for small groups.
 *
 * How it works:
 *
 * - it is initialized with a room ID and a map of "user group key" => "user count". The "group key" currently is the
 *   first letter of the user's first name
 * - loading a group of users can be triggered via the fetchUsers() action
 *   - once the users are loaded, they are also available via the getter "users"
 * - while the loading of a group is in progress, there is a promise available in loadingPromises(). Once the group is
 *   loaded, this promise is automatically fulfilled
 */

import { ActionTree, MutationTree } from 'vuex';
import { Actions, AugmentedActionContext, Getters, Mutations, MutationTypes, State } from './types/room-members.types';
import Api from '../../scripts/api';
import { Profile } from '../../scripts/api/models/user';
import UserList from './room-members/UserList';
import Feature from '@app/scripts/features';
import { Toggles } from '@features';

type UsersByKey = { [key: string]: Profile[] };

const state: State = {
    // required for initializing the component
    userCountByGroup: null,

    // required for initializing the component
    roomUid: null,

    // the actual list of users
    usersByGroup: new Map(),

    // keys that are already loaded
    loadedKeys: [],

    // keys that are currently being loaded
    loadingKeys: [],

    // Promises for groups that are currently being loaded
    loadingPromises: new Map(),

    // resolve functions for loadingPromises
    loadingPromiseResolvers: new Map(),
};

const getters: Getters = {
    groupsAvailable (state: State): boolean {
        return state.userCountByGroup !== null;
    },

    groupKeys (state: State): Iterable<string> {
        return Array.from(state.userCountByGroup?.keys() ?? []);
    },

    userListLoaded (state: State): (key: string) => boolean {
        return (key: string) => state.loadedKeys.indexOf(key) !== -1;
    },

    groupsAndCount (state: State): null | Map<string, number> {
        return state.userCountByGroup;
    },

    users (state: State): Map<string, UserList> {
        return state.usersByGroup;
    },
};

const actions: ActionTree<State, State> & Actions = {
    setRoomUid ({ commit }: AugmentedActionContext, roomUid: number) {
        commit(MutationTypes.SET_ROOM_UID, roomUid);
    },

    setGroups ({ commit } : AugmentedActionContext, groups: { [key: string]: number }): void {
        const groupsMap: Map<string, number> = new Map(Object.entries(groups));
        commit(MutationTypes.INITIALIZE_GROUPS, groupsMap);
    },

    async fetchUsers ({ commit }: AugmentedActionContext, key: string): Promise<Profile[]> {
        const totalUsersCount: number = Array.from(state.userCountByGroup?.values() ?? [])
            .reduce((sum: number, count: number) => sum + count);

        const keyToLoad: string = totalUsersCount <= 50 ? '*' : key;

        const keyIsAlreadyLoaded = state.loadedKeys.indexOf(key) > -1;
        const keyIsLoading = state.loadingKeys.indexOf(keyToLoad) > -1;

        const load = !keyIsAlreadyLoaded && !keyIsLoading;

        const promise = state.loadingPromises.get(key);
        if (keyIsLoading && promise) {
            return promise;
        }

        if (load) {
            commit(MutationTypes.START_LOADING, keyToLoad);

            if (keyToLoad === '*') {
                // fetching all users and then manually sorting into the correct sections
                const users: Profile[] = await Api.version(getApiVersion()).get(`rooms/${state.roomUid}/members`);

                const groupedUsers: UsersByKey = {};
                users.forEach((user: Profile) =>
                    groupedUsers[user.listKey]?.push(user) ?? (groupedUsers[user.listKey] = [user])
                );

                Object.entries(groupedUsers).forEach(
                    (data: [string, Profile[]]) => commit(MutationTypes.SET_USERS, { key: data[0], users: data[1] })
                );
            } else {
                const users: Profile[] = await Api.version(getApiVersion()).get(`rooms/${state.roomUid}/members?key=${key}`);

                if (users === undefined) {
                    throw new Error('Received no users');
                }
                commit(MutationTypes.SET_USERS, { key, users });
            }
        }

        return state.usersByGroup.get(key)?.users ?? [];
    },
};

const mutations: MutationTree<State> & Mutations = {
    SET_ROOM_UID (state: State, roomUid: number) {
        state.roomUid = roomUid;
    },

    INITIALIZE_GROUPS (state: State, groups: Map<string, number>) {
        state.userCountByGroup = groups;
        const usersByGroup: Map<string, UserList> =
            Array.from(groups.entries())
                .reduce<Map<string, UserList>>(
                    (acc: Map<string, UserList>, [key, userCount]: [string, number]) => acc.set(key, new UserList(userCount)),
                    new Map()
                );
        state.usersByGroup = usersByGroup;
        state.loadedKeys = [];
    },

    START_LOADING (state: State, key: string) {
        if (key === '*') {
            // push a promise + resolver for each key that we need
            Array.from(state.userCountByGroup?.keys() ?? []).forEach((key_: string) => {
                const promise = new Promise<Profile[]>(resolve => {
                    state.loadingPromiseResolvers.set(key_, resolve);
                });
                state.loadingPromises.set(key_, promise);
            });
        } else {
            // we started loading one specific key => make sure to resolve that
            const promise = new Promise<Profile[]>(resolve => {
                state.loadingPromiseResolvers.set(key, resolve);
            });
            state.loadingPromises.set(key, promise);
        }

        state.loadingKeys.push(key);
    },

    SET_USERS (state: State, payload: { key: string, users: Profile[] }) {
        const usersEntry = state.usersByGroup.get(payload.key);
        if (usersEntry !== undefined) {
            usersEntry.users = payload.users;
            state.loadedKeys.push(payload.key);
            state.loadingKeys.splice(state.loadingKeys.indexOf(payload.key, 1));

            const resolver = state.loadingPromiseResolvers.get(payload.key);
            if (resolver) {
                resolver(payload.users);
            }
        } else {
            console.error(`No users defined for key ${payload.key}`);
        }
    },
};

const getApiVersion = (): number => {
    return Feature.enabled(Toggles.FCP_6398_MEMBERLIST_V2) ? 2 : 1;
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
